/*
points = {x:number,y:number}[]
*/
export function pointsToLine(points, xIsLog, yIsLog) {
  if (!Array.isArray(points) || points.length < 2) return null;

  const effPoint = points.map((p) => {
    return {
      x: xIsLog ? Math.log10(p.x) : p.x,
      y: yIsLog ? Math.log10(p.y) : p.y,
    };
  });

  const n = effPoint.length;
  const sum = effPoint.reduce(
    (acc, { x, y }) => {
      acc.x += x;
      acc.y += y;
      acc.xx += x * x;
      acc.xy += x * y;
      return acc;
    },
    { x: 0, y: 0, xx: 0, xy: 0 },
  );

  const xMean = sum.x / n;
  const yMean = sum.y / n;
  const ssxx = sum.xx - n * xMean * xMean;
  const ssxy = sum.xy - n * xMean * yMean;

  if (Math.abs(ssxx) < Number.EPSILON) {
    return {
      isVertical: true,
      x: xMean,
      predict: () => null,
    };
  }

  const slope = ssxy / ssxx;
  const intercept = yMean - slope * xMean;

  if (n <= 2) {
    return {
      a: slope,
      b: intercept,
    };
  }

  const rss = effPoint.reduce((sum, { x, y }) => {
    const diff = y - (slope * x + intercept);
    return sum + diff * diff;
  }, 0);

  const variance = rss / (n - 2);

  const slopeError = Math.sqrt(variance / ssxx);

  const interceptError = Math.sqrt(variance * (1 / n + (xMean * xMean) / ssxx));

  const ssyy = effPoint.reduce((sum, { y }) => sum + (y - yMean) ** 2, 0);

  const r2 = 1 - rss / ssyy;

  return {
    a: slope,
    b: intercept,

    ua: slopeError,
    ub: interceptError,

    r2,
  };
}

/*
line:{
  a:number,
  b:number,
  ua:number | undef,
  ub:number | undef,
  r2:number
}
rightTop,leftBottom:{
  x:number,
  y:number
}
*/
export function lineToRelativePoints(line, rightTop, leftBottom,xLabel,yLabel) {
  if (!line) return null;

  const xConverter = xLabel.isLog ? Math.log10 : (x)=>x
  const yConverter = yLabel.isLog ? (y)=>10**y : (y)=>y
  const rightOutPointX = rightTop.x;
  const rightOutPointY = yConverter(line.a * xConverter(rightOutPointX) + line.b);

  const leftOutPointX = leftBottom.x;
  const leftOutPointY = yConverter(line.a * xConverter(leftOutPointX) + line.b);

  return [
    { x: rightOutPointX, y: rightOutPointY },
    { x: leftOutPointX, y: leftOutPointY },
  ];
}

function roundToSignificant(value, digits) {
  if (value === 0) return 0;

  const exponent = Math.floor(Math.log10(Math.abs(value)));
  const factor = 10 ** (digits - exponent - 1);

  return Math.round(value * factor) / factor;
}

function formatNumberForDisplay(value, digits = 3) {
  if (value === 0) return "0";

  const absValue = Math.abs(value);
  const exponent = Math.floor(Math.log10(absValue));

  if (exponent >= 4 || exponent <= -3) {
    const mantissa = value / 10 ** exponent;
    const roundedMantissa = roundToSignificant(mantissa, digits);
    return `${roundedMantissa} \\times 10^{${exponent}}`;
  }

  return `${roundToSignificant(value, digits)}`;
}

function roundByUncertainty(value, uncertainty) {
  if (uncertainty == null) {
    return { value: formatNumberForDisplay(value, 3), uncertainty: null };
  }

  const exponent = Math.floor(Math.log10(Math.abs(uncertainty)));
  const firstDigit = Math.floor(uncertainty / 10 ** exponent);

  const sig = firstDigit <= 3 ? 2 : 1;

  const roundedU = roundToSignificant(uncertainty, sig);

  const place = Math.floor(Math.log10(Math.abs(roundedU)));

  const factor = 10 ** place;

  const roundedValue = Math.round(value / factor) * factor;

  return {
    value: formatNumberForDisplay(roundedValue, 3),
    uncertainty: formatNumberForDisplay(roundedU, sig),
  };
}


export function lineToString(line, xLabel, yLabel) {
  if (!line) return "";

  if (!line.ua || !line.ub) {
    const a = formatNumberForDisplay(line.a, 3);
    const b = formatNumberForDisplay(line.b, 3);

    if (line.b < 0) {
      return `\\(${yLabel.isLog ? `\\log_\{10\}${yLabel.y}` : yLabel.y} = ${a}${xLabel.isLog ? `\\log_\{10\}${xLabel.x}` : xLabel.x}  ${b}\\)`;
    }
    return `\\(${yLabel.isLog ? `\\log_\{10\}${yLabel.y}` : yLabel.y} = ${a}${xLabel.isLog ? `\\log_\{10\}${xLabel.x}` : xLabel.x} + ${b}\\)`;
  }

  const a = roundByUncertainty(line.a, line.ua);
  const b = roundByUncertainty(line.b, line.ub);

  return `\\(${yLabel.isLog ? `\\log_\{10\}${yLabel.y}` : yLabel.y} = (${a.value} \\pm ${a.uncertainty})${xLabel.isLog ? `\\log_\{10\}${xLabel.x}` : xLabel.x} + (${b.value} \\pm ${b.uncertainty})\\)`;
}
